import React, {
  createContext,
  useState,
  useRef,
  useCallback,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { Activity, ActivityState, ActivityMetrics, Position } from "../types";
import { getTrackingService } from "../services/trackingService";
import { getMapDataSource } from "../services/mapDataSource";
import { settingsService } from "../services/settingsService";
import { TrackingData } from "../services/trackingProvider";
import { getActivityStorageService } from "../services/activityStorageService";
import { calculateDistance } from "../utils/geoUtils";
import { usePreferences } from "./PreferencesContext";
import debugx from "debugx";

const debug = debugx("bikedash:activity");

export type PauseReason = "manual" | "distance" | "inactivity" | null;

export interface ActivityContextValue {
  state: ActivityState;
  metrics: ActivityMetrics;
  currentPosition: Position | null;
  currentActivityId: string | null;
  activityStartTime: number | null;
  pauseReason: PauseReason;
  startActivity: () => void;
  stopActivity: () => void;
  pauseActivity: () => void;
  resumeActivity: () => void;
  updatePosition: (position: Position) => void;
  getElapsedTime: () => number;
  dismissAutoPause: () => void;
  loadHistoricalActivities: () => Promise<Activity[]>;
  deleteActivity: (id: string) => Promise<void>;
  exportActivityAsGPX: (id: string) => Promise<string>;
  mergeActivityMetrics: (
    mergedDistance: number,
    mergedStartTime: number,
    mergedPausedDuration: number,
  ) => void;
}

export const ActivityContext = createContext<ActivityContextValue | null>(null);

interface ActivityProviderProps {
  children: ReactNode;
}

const initialMetrics: ActivityMetrics = {
  distance: 0,
  duration: 0,
  currentSpeed: 0,
  avgSpeed: 0,
  maxSpeed: 0,
};

// Interface for persisted activity data
interface PersistedActivity {
  activityId: string | null;
  state: ActivityState;
  startTime: number;
  pausedDuration: number;
  pauseStartTime: number | null;
  totalDistance: number;
  metrics: ActivityMetrics;
  lastPosition: Position | null;
  lastUpdateTime: number;
}

const ACTIVITY_STORAGE_KEY = "bikedash_current_activity";
const MIN_MOVEMENT_THRESHOLD = 5; // meters - positions closer than this are considered GPS drift
const IDLE_TIME_THRESHOLD = 15000; // 15 seconds - time without movement to mark as idle
const METERS_PER_KM = 1000; // meters in a kilometer
const DEFAULT_INACTIVITY_TIMEOUT = 5; // minutes
const DEFAULT_INACTIVITY_DISTANCE = 2; // km

export function ActivityProvider({ children }: ActivityProviderProps) {
  const { userPreferences } = usePreferences();
  const [state, setState] = useState<ActivityState>("idle");
  const [metrics, setMetrics] = useState<ActivityMetrics>(initialMetrics);
  const [currentPosition, setCurrentPosition] = useState<Position | null>(null);
  const [currentActivityId, setCurrentActivityId] = useState<string | null>(
    null,
  );
  const [activityStartTime, setActivityStartTime] = useState<number | null>(
    null,
  );
  const [pauseReason, setPauseReason] = useState<PauseReason>(null);
  const currentActivityIdRef = useRef<string | null>(null);
  const lastPauseReasonRef = useRef<PauseReason>(null); // Track original pause reason even after alert dismissed
  const hasCheckedLocationSharing = useRef(false); // Track if we've checked location sharing on startup

  // Use refs for values that change frequently but shouldn't cause re-renders
  const startTimeRef = useRef<number | null>(null);
  const pausedDurationRef = useRef(0);
  const pauseStartTimeRef = useRef<number | null>(null);
  const lastPositionRef = useRef<Position | null>(null);
  const lastSavedPositionRef = useRef<Position | null>(null); // Track last saved position for drift filtering
  const idleStartTimeRef = useRef<number | null>(null); // Track when idle period started
  const lastMovementTimeRef = useRef<number | null>(null); // Track last time we had real movement
  const backgroundTimestampRef = useRef<number | null>(null); // Track when app went to background
  const backgroundPositionRef = useRef<Position | null>(null); // Position when app went to background
  const totalDistanceRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const trackingSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(
    null,
  );
  const storageServiceRef = useRef(getActivityStorageService());

  // Save activity to localStorage - no dependencies so it always uses current values
  const saveActivity = useCallback(() => {
    // Access current state through setState
    setState((currentState) => {
      if (currentState === "idle" || !startTimeRef.current) return currentState;

      // Access current metrics through setMetrics
      setMetrics((currentMetrics) => {
        const activityData: PersistedActivity = {
          activityId: currentActivityIdRef.current,
          state: currentState,
          startTime: startTimeRef.current!,
          pausedDuration: pausedDurationRef.current,
          pauseStartTime: pauseStartTimeRef.current,
          totalDistance: totalDistanceRef.current,
          metrics: currentMetrics,
          lastPosition: lastPositionRef.current,
          lastUpdateTime: Date.now(),
        };

        try {
          localStorage.setItem(
            ACTIVITY_STORAGE_KEY,
            JSON.stringify(activityData),
          );
          debug("Activity saved to localStorage", {
            state: currentState,
            distance: totalDistanceRef.current,
            duration: currentMetrics.duration,
          });
        } catch (error) {
          debug.error("Failed to save activity:", error);
        }

        return currentMetrics; // Return unchanged
      });

      return currentState; // Return unchanged
    });
  }, []); // No dependencies - always uses current values

  // Restore activity from localStorage
  const restoreActivity = useCallback(() => {
    try {
      const stored = localStorage.getItem(ACTIVITY_STORAGE_KEY);
      if (!stored) return false;

      const activityData: PersistedActivity = JSON.parse(stored);
      const timeSinceLastUpdate = Date.now() - activityData.lastUpdateTime;

      // Get inactivity timeout from settings (0 = disabled)
      const inactivityTimeoutMinutes =
        userPreferences.inactivityTimeout ?? DEFAULT_INACTIVITY_TIMEOUT;

      // Only restore if within timeout (or if timeout is disabled)
      if (inactivityTimeoutMinutes > 0) {
        const inactivityTimeoutMs = inactivityTimeoutMinutes * 60 * 1000;
        if (timeSinceLastUpdate > inactivityTimeoutMs) {
          debug(
            "Activity too old to restore (%dms since last update, timeout: %d min)",
            timeSinceLastUpdate,
            inactivityTimeoutMinutes,
          );
          localStorage.removeItem(ACTIVITY_STORAGE_KEY);
          return false;
        }
      }

      // Adjust times for the gap
      const now = Date.now();
      const gap = now - activityData.lastUpdateTime;

      // If activity was paused, extend the pause duration
      if (activityData.state === "paused" && activityData.pauseStartTime) {
        activityData.pausedDuration += gap;
      }

      // Restore state
      setState(activityData.state);
      startTimeRef.current = activityData.startTime;
      pausedDurationRef.current = activityData.pausedDuration;
      pauseStartTimeRef.current = activityData.pauseStartTime;
      totalDistanceRef.current = activityData.totalDistance;
      lastPositionRef.current = activityData.lastPosition;
      setMetrics(activityData.metrics);

      // Restore activity ID so OPFS updates continue
      if (activityData.activityId) {
        setCurrentActivityId(activityData.activityId);
        currentActivityIdRef.current = activityData.activityId;
      }
      if (activityData.startTime) {
        setActivityStartTime(activityData.startTime);
      }

      debug("Activity restored from localStorage (gap: %dms)", gap);
      return true;
    } catch (error) {
      debug.error("Failed to restore activity:", error);
      localStorage.removeItem(ACTIVITY_STORAGE_KEY);
      return false;
    }
  }, [userPreferences.inactivityTimeout]);

  const calculateDistanceWrapper = (pos1: Position, pos2: Position): number => {
    if (!pos1.lat || !pos1.lng || !pos2.lat || !pos2.lng) return 0;
    return calculateDistance(pos1.lat, pos1.lng, pos2.lat, pos2.lng);
  };

  const getElapsedTime = useCallback(() => {
    if (!startTimeRef.current) return 0;
    return Date.now() - startTimeRef.current - pausedDurationRef.current;
  }, []);

  // Shared function to update activity in OPFS
  const updateActivityInOPFS = useCallback(async () => {
    if (!currentActivityIdRef.current || !startTimeRef.current) return;

    const duration = getElapsedTime();
    try {
      const activity: Activity = {
        id: currentActivityIdRef.current,
        title: `Activity ${new Date(startTimeRef.current).toLocaleDateString()}`,
        startTime: startTimeRef.current,
        distance: totalDistanceRef.current,
        avgSpeed: totalDistanceRef.current / (duration / 1000),
        maxSpeed: metrics.maxSpeed,
        pausedDuration: pausedDurationRef.current,
      };
      await storageServiceRef.current.saveActivity(activity);
      debug("Updated activity in OPFS:", currentActivityIdRef.current);
    } catch (error) {
      debug.error("Failed to update activity in OPFS:", error);
    }
  }, [getElapsedTime, metrics.maxSpeed]);

  const startActivity = useCallback(async () => {
    debug.info("Starting activity");
    setState("recording");

    const activityId = `activity_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    setCurrentActivityId(activityId);
    currentActivityIdRef.current = activityId;

    const now = Date.now();
    startTimeRef.current = now;
    setActivityStartTime(now);
    pausedDurationRef.current = 0;
    totalDistanceRef.current = 0;
    lastPositionRef.current = null;
    lastSavedPositionRef.current = null; // Reset saved position for drift filtering
    setMetrics(initialMetrics);

    // Create initial activity in OPFS
    const activity: Activity = {
      id: activityId,
      title: `Activity ${new Date().toLocaleDateString()}`,
      startTime: startTimeRef.current,
      distance: 0,
      avgSpeed: 0,
      maxSpeed: 0,
      pausedDuration: 0,
    };

    try {
      await storageServiceRef.current.saveActivity(activity);
      await storageServiceRef.current.appendEvent(activityId, {
        timestamp: Date.now(),
        type: "start",
      });
      debug("Activity started in OPFS:", activityId);
    } catch (error) {
      debug.error("Failed to save activity to OPFS:", error);
    }

    // Start a timer that updates metrics every 5 seconds (not every second!)
    timerRef.current = setInterval(async () => {
      const duration = getElapsedTime();
      setMetrics((prev) => ({
        ...prev,
        duration,
        distance: totalDistanceRef.current,
        avgSpeed: totalDistanceRef.current / (duration / 1000),
      }));

      // Save activity periodically to localStorage
      saveActivity();

      // Check for idle timeout when GPS stops sending positions
      if (
        lastSavedPositionRef.current &&
        !lastSavedPositionRef.current.isIdle
      ) {
        const now = Date.now();
        const timeSinceLastPosition =
          now - lastSavedPositionRef.current.timestamp;
        if (timeSinceLastPosition >= IDLE_TIME_THRESHOLD) {
          // Been idle for 15+ seconds, save an idle marker
          debug(
            "Idle detected in timer: %d seconds since last position",
            timeSinceLastPosition / 1000,
          );
          const idlePosition: Position = {
            ...lastSavedPositionRef.current,
            isIdle: true,
            timestamp: now,
          };
          try {
            await storageServiceRef.current.appendTrackPoint(
              currentActivityIdRef.current!,
              idlePosition,
            );
            lastSavedPositionRef.current = idlePosition;
          } catch (error) {
            debug.error("Failed to save idle marker:", error);
          }
        }
      }

      // Also update activity in OPFS
      await updateActivityInOPFS();
    }, 5000);

    // Save immediately after starting
    setTimeout(saveActivity, 100);
  }, [getElapsedTime, updateActivityInOPFS]);

  const stopActivity = useCallback(async () => {
    const finalDuration = getElapsedTime();
    const finalDistance = totalDistanceRef.current;
    debug.info("Stopping activity", {
      distance: finalDistance,
      duration: finalDuration,
    });

    // Check if activity is too short to save
    // Don't save if: duration < 30s OR (duration < 5min AND distance < 0.25mi)
    const isTooShort =
      finalDuration < 30000 || // < 30 seconds
      (finalDuration < 300000 && finalDistance < 402.335); // < 5 minutes AND < 0.25 miles (402.335 meters)

    // Handle activity saving/deletion
    if (currentActivityIdRef.current && startTimeRef.current) {
      if (isTooShort) {
        // Activity is too short - delete it if it was already persisted
        try {
          await storageServiceRef.current.deleteActivity(
            currentActivityIdRef.current,
          );
          debug.warn(
            "Activity discarded (too short):",
            currentActivityIdRef.current,
            {
              distance: finalDistance,
              duration: finalDuration,
            },
          );
        } catch (error) {
          // Activity might not exist yet, which is fine
          debug("Activity not persisted yet, skipping delete");
        }
      } else {
        // Activity is long enough - save it normally
        try {
          const activity: Activity = {
            id: currentActivityIdRef.current,
            title: `Activity ${new Date(startTimeRef.current).toLocaleDateString()}`,
            startTime: startTimeRef.current,
            endTime: Date.now(),
            distance: finalDistance,
            avgSpeed: finalDistance / (finalDuration / 1000),
            maxSpeed: metrics.maxSpeed,
            pausedDuration: pausedDurationRef.current,
          };

          await storageServiceRef.current.saveActivity(activity);
          await storageServiceRef.current.appendEvent(
            currentActivityIdRef.current,
            {
              timestamp: Date.now(),
              type: "stop",
            },
          );
          debug(
            "Activity stopped and saved to OPFS:",
            currentActivityIdRef.current,
            {
              distance: activity.distance,
              duration: finalDuration,
              avgSpeed: activity.avgSpeed,
            },
          );
        } catch (error) {
          debug.error("Failed to save activity to OPFS:", error);
        }
      }
    }

    setState("idle");
    setCurrentActivityId(null);
    setActivityStartTime(null);
    currentActivityIdRef.current = null;
    startTimeRef.current = null;
    pausedDurationRef.current = 0;
    pauseStartTimeRef.current = null;
    lastPositionRef.current = null;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Clear persisted activity
    localStorage.removeItem(ACTIVITY_STORAGE_KEY);
    debug("Cleared persisted activity");
  }, [getElapsedTime, metrics.maxSpeed]);

  const pauseActivity = useCallback(
    async (reason: PauseReason = "manual") => {
      debug.info("Pausing activity, reason: %s", reason);
      setState("paused");
      setPauseReason(reason);
      lastPauseReasonRef.current = reason; // Store original reason for resume logic
      pauseStartTimeRef.current = Date.now();

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Save activity state when paused
      setTimeout(saveActivity, 100);

      // Mark last position as paused and save it
      if (currentActivityIdRef.current && lastSavedPositionRef.current) {
        try {
          // Create a new position marked as paused
          const pausedPosition: Position = {
            ...lastSavedPositionRef.current,
            isPaused: true,
            timestamp: Date.now(),
          };

          // Save this paused marker to track points
          await storageServiceRef.current.appendTrackPoint(
            currentActivityIdRef.current,
            pausedPosition,
          );

          debug("Marked last position as paused");
        } catch (error) {
          debug.error("Failed to mark pause position:", error);
        }
      }

      // Log pause event to OPFS
      if (currentActivityIdRef.current) {
        try {
          await storageServiceRef.current.appendEvent(
            currentActivityIdRef.current,
            {
              timestamp: Date.now(),
              type: "pause",
            },
          );
        } catch (error) {
          debug.error("Failed to log pause event:", error);
        }
      }
    },
    [currentActivityId],
  );

  const resumeActivity = useCallback(async () => {
    debug.info(
      "Resuming activity, last pause reason: %s",
      lastPauseReasonRef.current,
    );
    if (pauseStartTimeRef.current) {
      pausedDurationRef.current += Date.now() - pauseStartTimeRef.current;
    }
    pauseStartTimeRef.current = null;

    // Check if resuming from a distance-based auto-pause
    // Use the ref because the alert might have been dismissed (which clears pauseReason state)
    const wasDistancePause = lastPauseReasonRef.current === "distance";

    // If resuming from GPS jump, clear stored positions to start fresh
    // This prevents immediate re-triggering of the GPS jump detection
    if (wasDistancePause) {
      debug.info(
        "Resuming from GPS jump - clearing stored positions to start fresh",
      );
      lastSavedPositionRef.current = null;
      lastPositionRef.current = null;
      idleStartTimeRef.current = null;
      lastMovementTimeRef.current = null;
    }

    // Clear pause reason state and ref
    setPauseReason(null);
    lastPauseReasonRef.current = null;

    setState("recording");

    // Restart the timer
    timerRef.current = setInterval(async () => {
      const duration = getElapsedTime();
      setMetrics((prev) => ({
        ...prev,
        duration,
        distance: totalDistanceRef.current,
        avgSpeed: totalDistanceRef.current / (duration / 1000),
      }));

      // Save activity periodically
      saveActivity();

      // Check for idle timeout when GPS stops sending positions
      if (
        lastSavedPositionRef.current &&
        !lastSavedPositionRef.current.isIdle
      ) {
        const now = Date.now();
        const timeSinceLastPosition =
          now - lastSavedPositionRef.current.timestamp;
        if (timeSinceLastPosition >= IDLE_TIME_THRESHOLD) {
          // Been idle for 15+ seconds, save an idle marker
          debug(
            "Idle detected in timer: %d seconds since last position",
            timeSinceLastPosition / 1000,
          );
          const idlePosition: Position = {
            ...lastSavedPositionRef.current,
            isIdle: true,
            timestamp: now,
          };
          try {
            await storageServiceRef.current.appendTrackPoint(
              currentActivityIdRef.current!,
              idlePosition,
            );
            lastSavedPositionRef.current = idlePosition;
          } catch (error) {
            debug.error("Failed to save idle marker:", error);
          }
        }
      }

      // Also update activity in OPFS
      await updateActivityInOPFS();
    }, 5000);

    // Save immediately after resuming
    setTimeout(saveActivity, 100);

    // Log resume event to OPFS
    if (currentActivityIdRef.current) {
      try {
        await storageServiceRef.current.appendEvent(
          currentActivityIdRef.current,
          {
            timestamp: Date.now(),
            type: "resume",
          },
        );
      } catch (error) {
        debug.error("Failed to log resume event:", error);
      }
    }
  }, [getElapsedTime, currentActivityId]);

  const dismissAutoPause = useCallback(() => {
    debug.info("Dismissing auto-pause alert");
    setPauseReason(null);
  }, []);

  const updatePosition = useCallback(
    async (position: Position) => {
      debug("ActivityContext.updatePosition called:", {
        lat: position.lat,
        lng: position.lng,
        hasLat: !!position.lat,
        hasLng: !!position.lng,
        latType: typeof position.lat,
        lngType: typeof position.lng,
      });

      // Update MapDataSource with position data
      if (position.lat && position.lng) {
        try {
          debug("About to get mapDataSource");
          const mapDataSource = getMapDataSource();
          debug("Got mapDataSource, calling addRealPosition with:", {
            lat: position.lat,
            lng: position.lng,
            timestamp: position.timestamp,
          });
          mapDataSource.addRealPosition(position);
          debug("Successfully forwarded to mapDataSource");
        } catch (error) {
          debug.error("Error forwarding to mapDataSource:", error);
        }

        // Map updates now happen via MapSyncWrapper subscribing to mapDataSource
        // This ensures interpolated positions are used
      }

      // Store position in ref for internal use only
      setCurrentPosition(position);

      // Check for GPS jump on ANY position update (if enabled)
      if (
        state === "recording" &&
        lastSavedPositionRef.current &&
        lastSavedPositionRef.current.lat &&
        lastSavedPositionRef.current.lng
      ) {
        const inactivityDistanceKm =
          userPreferences.inactivityDistance ?? DEFAULT_INACTIVITY_DISTANCE;

        if (inactivityDistanceKm > 0) {
          const distanceJump = calculateDistanceWrapper(
            lastSavedPositionRef.current,
            position,
          );
          const distanceKm = distanceJump / METERS_PER_KM;
          const thresholdMeters = inactivityDistanceKm * METERS_PER_KM;

          if (distanceJump > thresholdMeters) {
            debug(
              "GPS jumped %.1f km (threshold: %d km) - auto-pausing",
              distanceKm,
              inactivityDistanceKm,
            );

            // Mark the last saved position as inactive
            if (currentActivityIdRef.current) {
              try {
                const inactivePosition: Position = {
                  ...lastSavedPositionRef.current,
                  isInactive: true,
                  timestamp: lastSavedPositionRef.current.timestamp,
                };

                await storageServiceRef.current.appendTrackPoint(
                  currentActivityIdRef.current,
                  inactivePosition,
                );

                debug("Marked position as inactive due to GPS jump");
              } catch (error) {
                debug.error("Failed to mark inactive position:", error);
              }
            }

            // Auto-pause the activity
            pauseActivity("distance");

            // Clear background tracking if it was set
            backgroundTimestampRef.current = null;
            backgroundPositionRef.current = null;

            return; // Don't process this position further
          }
        }
      }

      // Always update current speed and max speed regardless of recording state
      if (position.speed !== undefined) {
        setMetrics((prev) => {
          const newSpeed = position.speed || 0;
          const shouldUpdate =
            prev.currentSpeed !== newSpeed ||
            (state === "recording" && newSpeed > prev.maxSpeed);

          if (shouldUpdate) {
            return {
              ...prev,
              currentSpeed: newSpeed,
              maxSpeed:
                state === "recording"
                  ? Math.max(prev.maxSpeed, newSpeed)
                  : prev.maxSpeed,
            };
          }
          return prev;
        });
      }

      // Only update distance and other metrics if recording
      if (state !== "recording") return;
      if (!position.lat || !position.lng) return;

      // IMPORTANT: Only count real GPS positions for distance, not interpolated ones
      // Interpolated positions are for smooth animation only
      if (position.isInterpolated) {
        debug("Skipping interpolated position for distance calculation");
        return;
      }

      // Log track point to OPFS (only real GPS points)
      // Filter GPS drift - only save if moved more than 5 meters from last saved position
      let shouldSavePoint = false;
      let isIdlePoint = false;

      if (!lastSavedPositionRef.current) {
        // First position - always save
        shouldSavePoint = true;
        lastMovementTimeRef.current = position.timestamp;
      } else if (
        lastSavedPositionRef.current.lat &&
        lastSavedPositionRef.current.lng
      ) {
        const distanceFromLastSaved = calculateDistanceWrapper(
          lastSavedPositionRef.current,
          position,
        );

        if (distanceFromLastSaved >= MIN_MOVEMENT_THRESHOLD) {
          // Real movement detected
          shouldSavePoint = true;

          // Check if we were idle before this movement
          if (idleStartTimeRef.current && lastMovementTimeRef.current) {
            const idleDuration =
              position.timestamp - lastMovementTimeRef.current;
            if (idleDuration >= IDLE_TIME_THRESHOLD) {
              // We were idle for more than 15 seconds, mark the previous position as idle end
              debug("Idle period ended after %d seconds", idleDuration / 1000);
            }
          }

          // Reset idle tracking
          idleStartTimeRef.current = null;
          lastMovementTimeRef.current = position.timestamp;

          debug(
            "Movement detected: %dm from last saved position",
            distanceFromLastSaved.toFixed(1),
          );
        } else {
          // No significant movement - might be idle
          if (!idleStartTimeRef.current) {
            // Start tracking idle time
            idleStartTimeRef.current = position.timestamp;
            debug("Started tracking idle time");
          } else {
            const idleDuration = position.timestamp - idleStartTimeRef.current;
            if (
              idleDuration >= IDLE_TIME_THRESHOLD &&
              !lastSavedPositionRef.current.isIdle
            ) {
              // Been idle for 15+ seconds, save an idle marker
              shouldSavePoint = true;
              isIdlePoint = true;
              debug(
                "Idle time detected: %d seconds without movement",
                idleDuration / 1000,
              );
            }
          }

          if (!shouldSavePoint) {
            debug(
              "GPS drift filtered: %dm movement (below %dm threshold)",
              distanceFromLastSaved.toFixed(1),
              MIN_MOVEMENT_THRESHOLD,
            );
          }
        }
      }

      if (currentActivityIdRef.current && shouldSavePoint) {
        try {
          // Mark position as idle if needed
          const positionToSave = isIdlePoint
            ? { ...position, isIdle: true }
            : position;

          await storageServiceRef.current.appendTrackPoint(
            currentActivityIdRef.current,
            positionToSave,
          );
          // Update last saved position
          lastSavedPositionRef.current = positionToSave;

          // Also update the activity with current metrics periodically
          if (Math.random() < 0.1 && startTimeRef.current) {
            // Update ~10% of the time to avoid too many writes
            const duration = getElapsedTime();
            const activity: Activity = {
              id: currentActivityIdRef.current,
              title: `Activity ${new Date(startTimeRef.current).toLocaleDateString()}`,
              startTime: startTimeRef.current,
              distance: totalDistanceRef.current,
              avgSpeed: totalDistanceRef.current / (duration / 1000),
              maxSpeed: metrics.maxSpeed,
              pausedDuration: pausedDurationRef.current,
            };
            await storageServiceRef.current.saveActivity(activity);
          }
        } catch (error) {
          debug.error("Failed to log track point:", error);
        }
      }

      // Calculate distance if we have a previous position
      const lastPos = lastPositionRef.current;
      if (lastPos && lastPos.lat && lastPos.lng && !lastPos.isInterpolated) {
        const distance = calculateDistanceWrapper(lastPos, position);

        // Debug the calculation
        const timeDiff = position.timestamp - lastPos.timestamp;
        const speed = distance / (timeDiff / 1000); // m/s
        debug(
          "Distance calc: dist=%dm, time=%dms, speed=%dm/s (%dkm/h), from=[%s,%s] to=[%s,%s]",
          distance.toFixed(1),
          timeDiff,
          speed.toFixed(1),
          (speed * 3.6).toFixed(1),
          lastPos.lat?.toFixed(6),
          lastPos.lng?.toFixed(6),
          position.lat?.toFixed(6),
          position.lng?.toFixed(6),
        );

        if (distance > 5 && distance < 1000) {
          // Only count if moved more than 5m but less than 1km (sanity check)
          totalDistanceRef.current += distance;
          debug(
            "✅ Distance added: %dm added, total: %dm",
            distance.toFixed(1),
            totalDistanceRef.current.toFixed(1),
          );

          // Update max speed during recording
          setMetrics((prev) => {
            const newMaxSpeed = Math.max(prev.maxSpeed, position.speed || 0);

            // Only return new object if values actually changed
            if (newMaxSpeed !== prev.maxSpeed) {
              return {
                ...prev,
                maxSpeed: newMaxSpeed,
              };
            }
            return prev;
          });
        }
      }

      // Only store real GPS positions as last position for distance calculation
      if (!position.isInterpolated) {
        lastPositionRef.current = position;
      }
    },
    [
      state,
      pauseActivity,
      calculateDistanceWrapper,
      userPreferences.inactivityDistance,
    ],
  );

  // Subscribe to tracking service for position updates
  const startTrackingSubscription = useCallback(() => {
    if (trackingSubscriptionRef.current) return;

    const trackingService = getTrackingService();

    trackingSubscriptionRef.current = trackingService.subscribe(
      (data: TrackingData) => {
        const dataId = Math.random().toString(36).substring(2, 11);
        debug("Received tracking data:", {
          dataId,
          hasPosition: !!data.position,
          positionLat: data.position?.lat,
          positionLng: data.position?.lng,
          isActive: data.isActive,
          source: data.source,
        });
        if (data.position && data.isActive) {
          debug("Calling updatePosition", { dataId });
          updatePosition(data.position);
        }
      },
    );

    debug("Started tracking service subscription");
  }, [updatePosition]);

  // Methods for historical activities
  const loadHistoricalActivities = useCallback(async (): Promise<
    Activity[]
  > => {
    try {
      const activities = await storageServiceRef.current.listActivities();
      debug("Loaded %d historical activities", activities.length);
      return activities;
    } catch (error) {
      debug.error("Failed to load historical activities:", error);
      return [];
    }
  }, []);

  const deleteActivity = useCallback(async (id: string): Promise<void> => {
    try {
      await storageServiceRef.current.deleteActivity(id);
      debug("Deleted activity:", id);
    } catch (error) {
      debug.error("Failed to delete activity:", error);
      throw error;
    }
  }, []);

  const exportActivityAsGPX = useCallback(
    async (id: string): Promise<string> => {
      try {
        const gpx = await storageServiceRef.current.exportAsGPX(id);
        debug("Exported activity as GPX:", id);
        return gpx;
      } catch (error) {
        debug.error("Failed to export activity:", error);
        throw error;
      }
    },
    [],
  );

  const mergeActivityMetrics = useCallback(
    (
      mergedDistance: number,
      mergedStartTime: number,
      mergedPausedDuration: number,
    ) => {
      if (state === "idle" || !startTimeRef.current) {
        debug.warn("Cannot merge metrics when activity is not active");
        return;
      }

      debug(
        "Merging activity metrics - distance: %d, startTime: %d, pausedDuration: %d",
        mergedDistance,
        mergedStartTime,
        mergedPausedDuration,
      );

      // Add the merged distance to our total
      totalDistanceRef.current += mergedDistance;

      // Use the earlier start time
      if (mergedStartTime < startTimeRef.current) {
        startTimeRef.current = mergedStartTime;
        setActivityStartTime(mergedStartTime);
      }

      // Add the paused duration
      pausedDurationRef.current += mergedPausedDuration;

      // Update metrics immediately
      const duration = getElapsedTime();
      setMetrics((prev) => ({
        ...prev,
        distance: totalDistanceRef.current,
        avgSpeed: totalDistanceRef.current / (duration / 1000),
        // Keep current and max speed as they are
      }));

      // Save the updated activity
      updateActivityInOPFS();

      debug(
        "Updated metrics after merge - total distance: %d",
        totalDistanceRef.current,
      );
    },
    [state, getElapsedTime, updateActivityInOPFS],
  );

  // Stop tracking subscription
  const stopTrackingSubscription = useCallback(() => {
    if (trackingSubscriptionRef.current) {
      trackingSubscriptionRef.current.unsubscribe();
      trackingSubscriptionRef.current = null;
      debug("Stopped tracking service subscription");
    }
  }, []);

  // Restore activity on mount
  useEffect(() => {
    const restored = restoreActivity();
    if (restored) {
      // Immediately update metrics to show restored values
      const duration = getElapsedTime();
      setMetrics((prev) => ({
        ...prev,
        duration,
        distance: totalDistanceRef.current,
        avgSpeed: totalDistanceRef.current / (duration / 1000),
      }));

      // Check the restored state from refs/state, not the initial state
      // Need to check the actual restored state
      setState((restoredState) => {
        if (restoredState === "recording") {
          // If we restored a recording state, restart the timer
          timerRef.current = setInterval(async () => {
            const duration = getElapsedTime();
            setMetrics((prev) => ({
              ...prev,
              duration,
              distance: totalDistanceRef.current,
              avgSpeed: totalDistanceRef.current / (duration / 1000),
            }));

            // Save activity periodically to localStorage
            saveActivity();

            // Also update activity in OPFS
            await updateActivityInOPFS();
          }, 5000);
        }
        return restoredState;
      });
    }
  }, []); // Only run on mount

  // Start tracking subscription on mount
  useEffect(() => {
    startTrackingSubscription();

    return () => {
      stopTrackingSubscription();
    };
  }, [startTrackingSubscription, stopTrackingSubscription]);

  // Handle app backgrounding/foregrounding for auto-pause detection
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Only handle visibility changes when recording
      if (state !== "recording") return;

      if (document.hidden) {
        // App going to background - save timestamp and position
        backgroundTimestampRef.current = Date.now();
        backgroundPositionRef.current = lastSavedPositionRef.current;
        debug("App backgrounded at %s", new Date().toISOString());
      } else {
        // App coming to foreground - check if we should auto-pause
        if (backgroundTimestampRef.current && backgroundPositionRef.current) {
          const timeInBackground = Date.now() - backgroundTimestampRef.current;
          const timeInBackgroundMin = timeInBackground / 60000;

          debug(
            "App foregrounded after %d minutes",
            timeInBackgroundMin.toFixed(1),
          );

          let shouldAutoPause = false;
          let reason = "";

          // Get inactivity timeout from settings (0 = disabled)
          const inactivityTimeoutMinutes =
            userPreferences.inactivityTimeout ?? DEFAULT_INACTIVITY_TIMEOUT;

          // Check time threshold if enabled
          if (inactivityTimeoutMinutes > 0) {
            const inactivityTimeoutMs = inactivityTimeoutMinutes * 60 * 1000;
            if (timeInBackground > inactivityTimeoutMs) {
              shouldAutoPause = true;
              reason = `backgrounded for ${timeInBackgroundMin.toFixed(1)} minutes`;
            }
          }

          if (shouldAutoPause) {
            debug("Auto-pausing activity: %s", reason);

            // Mark the position before backgrounding as inactive
            if (currentActivityIdRef.current && backgroundPositionRef.current) {
              try {
                const inactivePosition: Position = {
                  ...backgroundPositionRef.current,
                  isInactive: true,
                  timestamp: backgroundTimestampRef.current,
                };

                await storageServiceRef.current.appendTrackPoint(
                  currentActivityIdRef.current,
                  inactivePosition,
                );

                debug("Marked position as inactive before auto-pause");
              } catch (error) {
                debug.error("Failed to mark inactive position:", error);
              }
            }

            // Auto-pause the activity
            pauseActivity("inactivity");

            // Clear background tracking
            backgroundTimestampRef.current = null;
            backgroundPositionRef.current = null;
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    state,
    pauseActivity,
    calculateDistanceWrapper,
    userPreferences.inactivityTimeout,
    userPreferences.inactivityDistance,
  ]);

  // Disable location sharing when app loads/returns if no activity is running
  useEffect(() => {
    const checkLocationSharing = () => {
      // Wait for activity restoration to complete before checking
      // If state is still "idle" after mount, it means no activity was restored
      if (hasCheckedLocationSharing.current) return;

      const isActivityRunning = state !== "idle";
      const shareLocation = userPreferences.shareLocationWithFriends;

      debug(
        "Checking location sharing on app startup/return: state=%s, shareLocation=%s",
        state,
        shareLocation,
      );

      if (!isActivityRunning && shareLocation) {
        debug(
          "No activity running on app startup/return - disabling location sharing",
        );
        settingsService.updateUserPreferences({
          shareLocationWithFriends: false,
        });
      } else if (isActivityRunning) {
        debug(
          "Activity is running (state=%s) - keeping location sharing as-is",
          state,
        );
      }

      hasCheckedLocationSharing.current = true;
    };

    // Delay check to allow activity restoration to complete
    const timeoutId = setTimeout(checkLocationSharing, 500);

    // Also check when visibility changes (app returns from background)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        hasCheckedLocationSharing.current = false;
        setTimeout(checkLocationSharing, 500);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [state, userPreferences.shareLocationWithFriends]);

  const value: ActivityContextValue = useMemo(
    () => ({
      state,
      metrics,
      currentPosition,
      currentActivityId,
      activityStartTime,
      pauseReason,
      startActivity,
      stopActivity,
      pauseActivity,
      resumeActivity,
      updatePosition,
      getElapsedTime,
      dismissAutoPause,
      loadHistoricalActivities,
      deleteActivity,
      exportActivityAsGPX,
      mergeActivityMetrics,
    }),
    [
      state,
      metrics,
      currentPosition,
      currentActivityId,
      activityStartTime,
      pauseReason,
      startActivity,
      stopActivity,
      pauseActivity,
      resumeActivity,
      updatePosition,
      getElapsedTime,
      dismissAutoPause,
      loadHistoricalActivities,
      deleteActivity,
      exportActivityAsGPX,
      mergeActivityMetrics,
    ],
  );

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
}
